import AccessProfileTypeNPS from "models/usuarios/access-profile-type-nps";
import { useEffect, useRef } from "react";
import { getNPSProductId, getNPSSrc, getShowNewNPS, getZone } from "utils/config-environment";
import Session from "utils/user-storage";

/** Cria e configura o script NPS */
function createNpsScript(config) {
  const { src, host, productId, authKey, storage, metadata } = config;
  const script = document.createElement('script');
  script.setAttribute('src', src);
  script.setAttribute('host', host);
  script.setAttribute('productId', productId);
  script.setAttribute('auth', authKey);
  script.setAttribute('storage', storage);
  script.setAttribute("metadata", JSON.stringify(metadata));
  return script;
}

/** Cria a configuração para o script NPS */
function createScriptConfig({ src, productId, authKey, storage, session }) {
  const profile = AccessProfileTypeNPS[session.PerfilAcesso];
  const zone = getZone();
  const metadata = { zone, userRole: profile };

  return { src, host: src, productId, authKey, storage, metadata };
}

/** Verifica se pode injetar o script */
function canInjectScript({ productId, idToken, scriptInjected }) {
  return !!(productId && idToken && getShowNewNPS() && !scriptInjected);
}

/** Obtém a sessão atual */
function getSession() {
  const session = Session.get();
  if (!session) {
    console.log('Session not created yet, skipping NPS script injection');
  }
  return session;
}

/** Injeta o script na página */
function injectScript({ idToken, session, src, productId, storage, authKey, scriptInjectedRef }) {
  localStorage.setItem(authKey, idToken);

  const config = createScriptConfig({
    src,
    productId,
    authKey,
    storage,
    session,
  });

  const script = createNpsScript(config);
  document.body.appendChild(script);
  scriptInjectedRef.current = true;
}

/** Remove o script da página */
function removeScript({ src, scriptInjectedRef }) {
  if (!scriptInjectedRef.current) return;

  const script = document.querySelector(`script[src='${src}']`);
  if (script) {
    document.body.removeChild(script);
  }
  scriptInjectedRef.current = false;
}

/** Hook principal para injetar o script NPS */
function useNewNpsScript(idToken) {
  const NPS_ID_TOKEN_KEY = 'NPS_ID_TOKEN';
  const NPS_STORAGE = 'local';
  const VITE_NPS_PRODUCT_ID = getNPSProductId();
  const VITE_NPS_SRC = getNPSSrc();
  const scriptInjectedRef = useRef(false);

  useEffect(() => {
    if (
      !canInjectScript({
        productId: VITE_NPS_PRODUCT_ID,
        idToken,
        scriptInjected: scriptInjectedRef.current,
      })
    ) {
      return;
    }

    function checkSessionAndInject() {
      if (scriptInjectedRef.current) return true;

      const session = getSession();
      if (!session) return false;

      injectScript({
        idToken,
        session,
        src: VITE_NPS_SRC,
        productId: VITE_NPS_PRODUCT_ID,
        storage: NPS_STORAGE,
        authKey: NPS_ID_TOKEN_KEY,
        scriptInjectedRef,
      });

      return true;
    }

    const injected = checkSessionAndInject();
    const intervalId = injected
      ? null
      : setInterval(() => {
          const done = checkSessionAndInject();
          if (done) clearInterval(intervalId);
        }, 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
      removeScript({ src: VITE_NPS_SRC, scriptInjectedRef });
    };
  }, [idToken, VITE_NPS_PRODUCT_ID, VITE_NPS_SRC]);

  return null;
}

export { useNewNpsScript };